import { useState, useCallback, useEffect, useRef } from "react";
import Cropper from "react-easy-crop";
import Resizer from "react-image-file-resizer";
import Button from "../buttons/buttons";

function getCroppedImg(imageSrc, croppedAreaPixels) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;
    image.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject("Canvas context not found");
        return;
      }

      canvas.width = croppedAreaPixels.width;
      canvas.height = croppedAreaPixels.height;

      ctx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height
      );

      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject("Canvas is empty");
      }, "image/jpeg");
    };

    image.onerror = () => reject("Image load error");
  });
}

const ImageCropUploader = (props) => {
  const {
    width = 200,
    height = 200,
    images,
    onClose,
    getCroppedImages,
  } = props;
  const [imageSrc, setImageSrc] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // useRef to accumulate final files without causing re-renders
  const croppedFilesRef = useRef([]);

  // Convert File → base64 so Cropper can use it
  useEffect(() => {
    if (images && images[currentIndex]) {
      const file = images[currentIndex];
      const reader = new FileReader();
      reader.onload = () => setImageSrc(reader.result);
      reader.readAsDataURL(file);
    }
  }, [images, currentIndex]);

  const onCropComplete = useCallback((_, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleCrop = useCallback(async () => {
    if (!imageSrc || !croppedAreaPixels) return;

    const croppedBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

    const croppedFile = new File(
      [croppedBlob],
      `${images[currentIndex].name}.jpg`,
      { type: "image/jpeg" }
    );

    Resizer.imageFileResizer(
      croppedFile,
      width, // width
      height, // height
      "JPEG",
      80, // quality
      0,
      (file) => {
        // accumulate result
        croppedFilesRef.current.push(file);

        if (currentIndex < images.length - 1) {
          // move to next image
          setCurrentIndex((prev) => prev + 1);
        } else {
          // finished all images -> send to parent and close
          getCroppedImages([...croppedFilesRef.current]);
          // reset ref for next time (optional)
          croppedFilesRef.current = [];
          onClose();
        }
      },
      "file"
    );
  }, [
    imageSrc,
    croppedAreaPixels,
    currentIndex,
    images,
    width,
    height,
    getCroppedImages,
    onClose,
  ]);

  return (
    <div className="w-full">
      {/* <input type="file" accept="image/*" onChange={onFileChange} /> */}

      {imageSrc && (
        <div className="relative w-full h-80 bg-gray-200">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={width / height} // Square crop (good for product images)
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>
      )}

      <div className="w-full text-center py-4">
        <p className="text-sm text-gray-500 dark:text-white mb-2">
          Cropping image {currentIndex + 1} of {images.length}
        </p>
        <Button.Primary
          title={
            currentIndex === images.length - 1
              ? "Finish Cropping"
              : "Crop & Next"
          }
          onClick={handleCrop}
          type="button"
          className="mt-6"
        />
      </div>

      {/* {imageSrc && (
        <div className="w-full text-center py-4">
          <Button.Primary
            title={"Crop & Resize"}
            onClick={handleCrop}
            type="button"
            className="mt-6"
          />
        </div>
      )} */}
    </div>
  );
};

export default ImageCropUploader;
